import { IonIcon, IonItem, IonLabel } from "@ionic/react";
import { checkmarkDone } from "ionicons/icons";
import React, { useRef, useState } from "react";
import ImageUploadButton from "./ImageUploadButton";
import { dataUrlToFile, takePicture } from "../hooks/common";
import { useImageUpload } from "../hooks/useImageUpload";
import { patchInventoryLocation, uploadDocument } from "../services/inventoryLocation.service";

// Shop-front photo + GST/registration certificate upload controls, shared
// by CreateInventoryLocation.tsx and EditInventoryLocation.tsx. Both
// controls use the same icon-driven affordance (ImageUploadButton) so they
// look and behave consistently -- the GST picker used to be a bare native
// <input type="file">, which rendered nothing like the photo control.
//
// Two modes:
// - immediate (default, `deferred` unset) -- used by EditInventoryLocation,
//   where the location already exists: picking a file uploads it and
//   patches the location right away. existingPhotoUrl/existingGstUrl seed
//   the "already uploaded" state so edit mode shows a thumbnail/"View"
//   link instead of pretending nothing's there.
// - deferred (`deferred` true) -- used by CreateInventoryLocation, where no
//   location id exists yet (the upload endpoints are keyed by location id
//   in the URL, so they're impossible before creation). Picking a file only
//   captures it locally (preview + onPhotoSelected/onGstSelected); the
//   caller uploads the real files itself once the location has been
//   created, right after the same submit that creates it.
interface Props {
  locationId?: string;
  deferred?: boolean;
  disabled?: boolean;
  existingPhotoUrl?: string;
  existingGstUrl?: string;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
  onPhotoSelected?: (file: File) => void;
  onGstSelected?: (file: File) => void;
}

const ChannelPartnerMediaUploads: React.FC<Props> = ({
  locationId,
  deferred,
  disabled,
  existingPhotoUrl,
  existingGstUrl,
  onSuccess,
  onError,
  onPhotoSelected,
  onGstSelected,
}) => {
  const [photoUrl, setPhotoUrl] = useState(existingPhotoUrl ?? "");
  const [gstUrl, setGstUrl] = useState(existingGstUrl ?? "");
  const [gstFileName, setGstFileName] = useState("");
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [capturingPhoto, setCapturingPhoto] = useState(false);
  const gstInputRef = useRef<HTMLInputElement>(null);

  const { isUploading: isUploadingPhoto, uploadImage } = useImageUpload({
    primaryId: locationId ?? "",
    secondaryId: "shopFront",
    onSuccess: (body) => {
      const url = body?.images?.[0];
      patchInventoryLocation(locationId!, { shopFrontPhotoUrl: url })
        .then(() => {
          setPhotoUrl(url);
          onSuccess("Shop front photo saved");
        })
        .catch((err: any) => onError(err?.message ?? String(err)));
    },
    onError: (err) => onError(err),
  });

  async function captureShopFrontPhoto() {
    setCapturingPhoto(true);
    try {
      const dataUrl = await takePicture();
      if (!dataUrl) return;
      const file = await dataUrlToFile(dataUrl);
      setPhotoUrl(dataUrl);
      onPhotoSelected?.(file);
    } catch (err: any) {
      onError(err?.message ?? String(err));
    } finally {
      setCapturingPhoto(false);
    }
  }

  function handleGstFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (deferred) {
      setGstFileName(file.name);
      onGstSelected?.(file);
      return;
    }

    setUploadingDocument(true);
    uploadDocument(file)
      .then((res) => {
        const url = res?.url?.[0];
        return patchInventoryLocation(locationId!, { gstCertificateUrl: url }).then(() => url);
      })
      .then((url) => {
        setGstUrl(url);
        onSuccess("GST certificate saved");
      })
      .catch((err: any) => onError(err?.message ?? String(err)))
      .finally(() => setUploadingDocument(false));
  }

  const photoReady = deferred ? !!photoUrl && !capturingPhoto : !!photoUrl && !isUploadingPhoto;
  const gstReady = deferred ? !!gstFileName : !!gstUrl && !uploadingDocument;

  return (
    <>
      <IonItem>
        {photoReady && (
          <img
            src={photoUrl}
            alt="Shop front"
            slot="start"
            style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 4 }}
          />
        )}
        <IonLabel>
          Shop Front Photo
          {photoReady && (
            <p style={{ color: "var(--ion-color-success)" }}>
              <IonIcon icon={checkmarkDone} style={{ verticalAlign: "middle" }} /> {deferred ? "Selected" : "Uploaded"}
            </p>
          )}
        </IonLabel>
        <ImageUploadButton
          isUploading={deferred ? capturingPhoto : isUploadingPhoto}
          onUpload={deferred ? captureShopFrontPhoto : uploadImage}
          disabled={disabled}
        />
      </IonItem>

      <IonItem>
        <IonLabel>
          GST / Shop Registration Certificate
          {gstReady && (
            <p style={{ color: "var(--ion-color-success)" }}>
              <IonIcon icon={checkmarkDone} style={{ verticalAlign: "middle" }} />{" "}
              {deferred ? (
                gstFileName
              ) : (
                <>
                  Uploaded ·{" "}
                  <a href={gstUrl} target="_blank" rel="noreferrer">
                    View
                  </a>
                </>
              )}
            </p>
          )}
        </IonLabel>
        <ImageUploadButton isUploading={uploadingDocument} onUpload={() => gstInputRef.current?.click()} disabled={disabled} />
        <input
          ref={gstInputRef}
          type="file"
          accept=".pdf,.doc,.docx,image/jpeg,image/png"
          style={{ display: "none" }}
          onChange={handleGstFileChange}
        />
      </IonItem>
    </>
  );
};

export default ChannelPartnerMediaUploads;
